import React, { useEffect, useMemo, useState } from 'react';
import { Button, Divider, Stack, Tooltip } from '@mui/material';
import { NiaraIcon } from '@niaratech/niara-icons';
import { Form, FormInput, InputDatePicker, InputText, InputTime } from '@niaratech/niara-react-form';
import { type AddonResult } from '@niarab2c/niarab2c-core/src/app/reducers/addon/addonSearch';
import { printPrice } from '@niaratech/niara-js-commons';
import { type AddonCartItem } from '@niarab2c/niarab2c-core/src/app/reducers/shoppingCart';
import { ImageGallery } from '../../components';
import { SelectButton } from '../../microComponents';
import * as S from './styles';
export type AddonCardDetailsProps = {
  startDate?: string;
  endDate?: string;
  addonResult?: AddonResult;
  onCartClick?: (variants: FormValue['variants'], addonResult: AddonResult) => void;
  onSubmit?: (variants: FormValue['variants'], addonResult: AddonResult) => void;
  addonCartItems?: AddonCartItem[]; // apenas se quiser que venha preenchido com os itens do carrinho
  hideBookingButton?: boolean;
  showHeaderOnly?: boolean;
};
export type FormValue = {
  variants?: {
    variantId: string;
    variantLabel: string;
    quantity: number;
    date?: string[];
    time?: string[];
    extraFields?: {
      [key: string]: string;
    }[];
  }[];
};
const convertAddonCartItemsToFormValue = (addonCartItems: AddonCartItem[], addonResult?: AddonResult): FormValue => {
  if (addonCartItems?.length <= 0) {
    return {
      variants: addonResult?.addon?.priceByVariant?.map(variant => ({
        variantId: variant?.id,
        variantLabel: variant?.label,
        quantity: null
      }))
    };
  }
  const addon = addonResult?.addon;
  if (addon?.pricingType !== 'ROOM') {
    const variants: FormValue['variants'] = addon?.priceByVariant?.map(variant => ({
      variantId: variant?.id,
      variantLabel: variant?.label,
      quantity: addonCartItems?.filter(item => item?.addonResult?.addonId === addon?.id && item?.variantId == variant?.id)?.length || null,
      date: addonCartItems?.filter(item => item?.addonResult?.addonId === addon?.id && item?.variantId == variant?.id)?.map(item => item?.scheduleDate),
      time: addonCartItems?.filter(item => item?.addonResult?.addonId === addon?.id && item?.variantId == variant?.id)?.map(item => item?.scheduleTime),
      extraFields: addonCartItems?.filter(item => item?.addonResult?.addonId === addon?.id && item?.variantId == variant?.id)?.map(item => item?.extraFields?.reduce((acc, extra) => ({
        ...acc,
        [extra?.name]: extra?.valueStr || extra?.valueInt?.toString()
      }), {}))
    }));
    return {
      variants
    };
  } else {
    const variants: FormValue['variants'] = [{
      variantId: undefined,
      variantLabel: undefined,
      quantity: addonCartItems?.filter(item => item?.addonResult?.addonId === addon?.id)?.length || null,
      date: addonCartItems?.filter(item => item?.addonResult?.addonId === addon?.id)?.map(item => item?.scheduleDate),
      time: addonCartItems?.filter(item => item?.addonResult?.addonId === addon?.id)?.map(item => item?.scheduleTime),
      extraFields: addonCartItems?.filter(item => item?.addonResult?.addonId === addon?.id)?.map(item => item?.extraFields?.reduce((acc, extra) => ({
        ...acc,
        [extra?.name]: extra?.valueStr || extra?.valueInt?.toString()
      }), {}))
    }];
    return {
      variants
    };
  }
};
const AddonCardDetails = ({
  addonResult,
  onCartClick,
  onSubmit,
  startDate,
  endDate,
  addonCartItems,
  hideBookingButton,
  showHeaderOnly
}: AddonCardDetailsProps): JSX.Element => {
  const addon = addonResult?.addon;
  const results = addonResult?.results;
  const showVariantContent = addon?.schedule || addon?.extraFieldDefinitions?.length > 0;
  const [value, setValue] = useState<FormValue>(convertAddonCartItemsToFormValue(addonCartItems, addonResult));
  const [prevValue, setPrevValue] = useState<FormValue>(value);
  const [submitAction, setSubmitAction] = useState<'cart' | 'checkout'>('cart');
  const handleCartClick = () => {
    setSubmitAction('cart');
  };
  const handleCheckoutClick = () => {
    setSubmitAction('checkout');
  };
  const handleSubmit = (formValues: FormValue) => {
    if (submitAction === 'cart') {
      onCartClick?.(formValues?.variants, addonResult);
    } else {
      onSubmit?.(formValues?.variants, addonResult);
    }
  };
  const totalValue = useMemo(() => value?.variants?.reduce((acc, variant) => {
    const result = results?.find(result => result?.variantId == variant?.variantId);
    const price = result?.priceComposition?.total;
    return acc + (price?.value ?? 0) * (variant?.quantity ?? 0);
  }, 0), [results, value?.variants]);
  const currency = useMemo(() => results?.[0]?.priceComposition?.total?.currency, [results]);

  // Ao adicionar uma nova quantidade, atualiza a data e hora default no caso de houver agendamento
  useEffect(() => {
    if (!addonResult?.addon?.schedule) return;
    if (value?.variants) {
      const newVariants = value.variants.map((variant, variantIndex) => {
        const prevVariant = prevValue?.variants?.[variantIndex];
        const prevQuantity = prevVariant?.quantity || 0;
        const currentQuantity = variant.quantity || 0;

        // Se a quantidade aumentou
        if (currentQuantity > prevQuantity) {
          // Preencher automaticamente date e time para os novos itens
          const date = [...(variant.date || [])];
          const time = [...(variant.time || [])];

          // Para cada novo item adicionado
          for (let i = prevQuantity; i < currentQuantity; i++) {
            date[i] = startDate;
            time[i] = '10:00';
          }
          return {
            ...variant,
            date,
            ...(addonResult?.addon?.schehuleType === 'DATETIME' ? {
              time
            } : {})
          };
        }
        return variant;
      });

      // Atualiza o valor apenas se houve mudanças
      if (JSON.stringify(newVariants) !== JSON.stringify(value.variants)) {
        setValue({
          ...value,
          variants: newVariants
        });
      }
    }
  }, [addonResult?.addon?.schedule, addonResult?.addon?.schehuleType, prevValue?.variants, startDate, value]);
  useEffect(() => {
    setPrevValue(value);
  }, [value]);
  return <Form value={value} setValue={setValue} onSubmit={handleSubmit}>
      <S.Container>
        <S.Content>
          <S.Header>
            {addon?.images?.length > 0 && <S.Image>
                <ImageGallery dataGallery={addon?.images?.map(img => img?.url)} isImageMosaic={addon?.images?.length >= 4} customMosaicHeight="350px" showGalleryBar={false} />
              </S.Image>}
            <S.Title>{addon?.name}</S.Title>
            <S.Description>{addon?.description?.join('\n')}</S.Description>
          </S.Header>
          {!showHeaderOnly && <>
              <Divider orientation="horizontal" flexItem />
              <Stack direction="column" spacing="30px" divider={<Divider />}>
                {results?.map((result, variantIndex) => {
              const variant = addon?.priceByVariant?.find(variant => variant?.id === result?.variantId);
              const ageLabel = variant?.fromAge && variant?.toAge ? variant?.toAge > 100 ? `A partir de ${variant?.fromAge} anos` : `${variant?.fromAge} a ${variant?.toAge} anos` : variant?.fromAge ? `A partir de ${variant?.fromAge} anos` : variant?.toAge ? `Até ${variant?.toAge} anos` : null;
              const price = result?.priceComposition?.total;
              return <S.Variant key={result?.id}>
                      <S.VariantHeader>
                        <S.VariantHeaderDetails>
                          <S.VariantTitleGroup>
                            <S.VariantTitle>{result?.name}</S.VariantTitle>
                            {ageLabel && <S.VariantAge>{ageLabel}</S.VariantAge>}
                          </S.VariantTitleGroup>
                          {price?.value && (!value?.variants?.[variantIndex]?.quantity || !showVariantContent) ? <S.VariantPrice>{printPrice(price?.value, price?.currency)}</S.VariantPrice> : <S.VariantPrice>{"Grátis"}</S.VariantPrice>}
                        </S.VariantHeaderDetails>
                        <S.VariantQuantity>
                          <FormInput component={SelectButton} fieldPath={`variants.${variantIndex}.quantity`} highlighted maxValue={10} hideFieldLabel hideButtonIcon buttonLabel="Selecionar" />
                        </S.VariantQuantity>
                      </S.VariantHeader>
                      {showVariantContent && <S.VariantContent>
                          {Array.from({
                    length: value?.variants?.[variantIndex]?.quantity
                  }).map((_, qtyIndex) => <S.VariantItem key={qtyIndex}>
                              <S.VariantHeaderDetails>
                                <S.VariantTitle>{addon?.name}</S.VariantTitle>
                                <S.VariantPrice>{printPrice(price?.value, price?.currency)}</S.VariantPrice>
                              </S.VariantHeaderDetails>
                              <S.VariantItemOptions>
                                {addon?.schedule && <S.VariantSchedule>
                                    <FormInput component={InputDatePicker} fieldPath={`variants.${variantIndex}.date.${qtyIndex}`} label="Dia" required min={startDate} max={endDate} />
                                    {addon?.schehuleType === 'DATETIME' && <FormInput component={InputTime} fieldPath={`variants.${variantIndex}.time.${qtyIndex}`} label="Hora" required />}
                                  </S.VariantSchedule>}
                                {addon?.extraFieldDefinitions?.length > 0 && <S.VariantExtras>
                                    {addon?.extraFieldDefinitions?.map((extra, idx) => {
                          const isValidOption = v => {
                            if (extra?.subtype === 'OPTIONS' && extra?.inputType === 'textfield') {
                              const isValid = extra?.options?.split('\n')?.includes(v?.[extra?.name] as string);
                              const isEmptyAndNotRequired = !extra?.required && !v?.[extra?.name];
                              if (!isValid && !isEmptyAndNotRequired) {
                                return false;
                              }
                            }
                            return true;
                          };
                          return <S.VariantExtra key={idx}>
                                          <FormInput component={InputText} fieldPath={`variants.${variantIndex}.extraFields.${qtyIndex}.${extra?.name}`} label={extra?.question ? extra?.question : extra?.label} hint={extra?.hint} maxLength={extra?.maxLength} minLength={extra?.minLength} options={extra?.inputType === 'select' ? extra?.options?.split('\n')?.map(opt => ({
                              value: opt,
                              label: opt
                            })) : undefined} required={extra?.required} uppercase={extra?.uppercase} lowercase={extra?.lowercase} TextFieldProps={{
                              inputProps: {
                                pattern: extra?.pattern,
                                'data-testid': `${extra?.name}`
                              }
                            }} validator={v => {
                              return extra?.pattern && !RegExp(extra?.pattern, 'g').test(v) ? {
                                errors: ["Formato inválido"]
                              } : !isValidOption(v) ? {
                                errors: ["Valor inválido"]
                              } : undefined;
                            }} fullWidth />
                                        </S.VariantExtra>;
                        })}
                                  </S.VariantExtras>}
                              </S.VariantItemOptions>
                            </S.VariantItem>)}
                        </S.VariantContent>}
                    </S.Variant>;
            })}
              </Stack>
            </>}
        </S.Content>
        {!showHeaderOnly && <>
            <S.Footer>
              <S.Price>
                <S.TotalLabel>{"Total a pagar"}</S.TotalLabel>
                <S.TotalValue>{printPrice(totalValue, currency)}</S.TotalValue>
              </S.Price>
              <S.Buttons>
                {onCartClick && <Tooltip title={!hideBookingButton ? "Adicionar ao carrinho" : undefined}>
                    <Button type="submit" variant={hideBookingButton ? 'contained' : 'outlined'} color={hideBookingButton ? 'success' : 'textColor'} disabled={!value?.variants?.some(variant => variant?.quantity > 0)} onClick={handleCartClick}>
                      {hideBookingButton ? "Adicionar ao carrinho" : <NiaraIcon icon="sysShoppingAddCart" size={20} />}
                    </Button>
                  </Tooltip>}
                {hideBookingButton ? null : <Button type="submit" variant="contained" color="success" disabled={!value?.variants?.some(variant => variant?.quantity > 0)} onClick={handleCheckoutClick}>
                    {"Reservar agora"}
                  </Button>}
              </S.Buttons>
            </S.Footer>
          </>}
      </S.Container>
    </Form>;
};
export default AddonCardDetails;